<script lang="ts">
    import { onMount } from 'svelte/internal'
    import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons'
    import Popover from './popover.svelte'
    import Zinput from './z/zinput.svelte'
    import Zbutton from './z/zbutton.svelte'
    import ZContainer from './z/z-container.svelte'
    import { reauth, type ReauthChallenge } from '../../modules/infra/reauth.service'

    // O backend manda a chave técnica da ação; quem traduz para o usuário é a interface.
    // Ação sem rótulo aqui ainda funciona — só aparece com o texto genérico.
    const labels: Record<string, string> = {
        'charge.refund': 'Estornar esta cobrança',
    }

    let show = false
    let password = ''
    let message = ''
    let working = false
    let challenge: ReauthChallenge = null
    let confirmPassword: (password: string) => Promise<string> = null
    let resolver: (token: string | null) => void = null

    onMount(() => {
        reauth.registerPrompt((newChallenge, confirm) => {
            challenge = newChallenge
            confirmPassword = confirm
            password = ''
            message = ''
            working = false
            show = true
            window.api.users.statistics.tick('reauth.prompt') // conta cada exibição, não só a primeira: mede fricção real, não só presença
            return new Promise((resolve) => {
                resolver = resolve
            })
        })
    })

    function finish(token: string | null) {
        show = false
        const done = resolver
        resolver = null
        if (done) done(token)
    }

    async function onConfirm() {
        if (!password || working) return
        try {
            working = true
            message = ''
            const token = await confirmPassword(password)
            finish(token)
        } catch (error) {
            // erra a senha e continua aqui: fechar a janela jogaria o erro na tela de trás, longe do campo
            message = typeof error === 'string' ? error : 'Não foi possível confirmar a senha'
            password = ''
        } finally {
            working = false
        }
    }

    function onCancel() {
        finish(null)
    }

    function onKeydown(event: KeyboardEvent) {
        if (!show) return
        if (event.key === 'Enter') onConfirm()
    }

    $: acao = labels[challenge?.action] || 'Concluir esta ação'
</script>

<!-- Enter confirma: o listener é de janela porque o modal é a única coisa em foco enquanto aberto -->
<svelte:window on:keydown={onKeydown} />

{#if show}
    <!-- o modal abre no meio de uma chamada, quando a tela de trás quase sempre está com o LoadSpinner
         ligado — e ele põe pointer-events:none no <body>. Sem reativar aqui, o modal fica sem clique. -->
    <div class="reauth-layer">
        <Popover title="Confirme sua senha" width="440px" height="auto" padding="16px" on:close={onCancel}>
            <div>
                <p class="reauth-headline">{acao} exige a sua senha, por ser uma operação sensível.</p>
                <ZContainer gapRow="8px" gapCol="8px">
                    <Zinput label="Sua senha" size={12} type="password" bind:value={password} />
                </ZContainer>
                {#if message}
                    <div class="reauth-error">{message}</div>
                {/if}
                <div class="reauth-acts">
                    <Zbutton color="secondary" icon={faXmark} label="Cancelar" on:click={onCancel} />
                    <Zbutton
                        color="primary"
                        icon={faCheck}
                        label="Confirmar"
                        loading={working}
                        disabled={working || !password}
                        on:click={onConfirm}
                    />
                </div>
            </div>
        </Popover>
    </div>
{/if}

<style>
    .reauth-layer {
        pointer-events: auto;
    }
    .reauth-headline {
        font-size: 14px;
        opacity: 0.8;
        margin-bottom: 12px;
    }
    .reauth-error {
        margin-top: 8px;
        font-size: 13px;
        color: #c34970;
    }
    .reauth-acts {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 16px;
    }
</style>
